/**
 * Interactive Setup Wizard
 * Step-by-step guide for setting up ADK development environment
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CodeBlock } from "@components/common";
import {
  TerminalInstructions,
  PythonInstallHelper,
  ApiKeyInput,
  ExpandableSection,
  TroubleshootingSection,
  type TroubleshootingItem,
} from "@components/setup";
import { useUserSettings } from "@hooks/useUserSettings";
import {
  detectPlatform,
  getPlatformName,
  getPlatformIcon,
  getPythonCommand,
  getVenvActivateCommand,
  getVenvCreatePath,
  getShellType,
  type Platform,
} from "@utils/platform";
import { cn } from "@utils/cn";

type SetupStep =
  | "welcome"
  | "prerequisites"
  | "venv"
  | "install-adk"
  | "api-key"
  | "verify"
  | "complete";

interface Step {
  id: SetupStep;
  title: string;
  description: string;
}

const steps: Step[] = [
  { id: "welcome", title: "Welcome", description: "Overview" },
  {
    id: "prerequisites",
    title: "Prerequisites",
    description: "Check requirements",
  },
  { id: "venv", title: "Virtual Environment", description: "Create workspace" },
  {
    id: "install-adk",
    title: "Install ADK",
    description: "Install Google ADK",
  },
  { id: "api-key", title: "API Key", description: "Configure access" },
  { id: "verify", title: "Verify", description: "Test installation" },
  { id: "complete", title: "Complete", description: "You're ready!" },
];

export function SetupWizard() {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [builderConnected, setBuilderConnected] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const {
    settings,
    updateSetting,
    earnBadge,
    getHealthUrl,
    completeSetupStep,
  } = useUserSettings();

  // Use currentSetupStep from settings, default to 'welcome'
  const currentStep = settings.currentSetupStep || "welcome";
  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  // Check if Visual Builder is running
  useEffect(() => {
    const abortController = new AbortController();

    const checkBuilder = async () => {
      try {
        await fetch(getHealthUrl(), {
          method: "HEAD",
          mode: "no-cors",
          signal: abortController.signal,
        });
        setBuilderConnected(true);
      } catch (error) {
        // Only update state if not aborted
        if (error instanceof Error && error.name !== "AbortError") {
          setBuilderConnected(false);
        }
      }
    };

    if (currentStep === "verify" || currentStep === "complete") {
      checkBuilder();
      const interval = setInterval(checkBuilder, 5000);
      return () => {
        abortController.abort();
        clearInterval(interval);
      };
    }

    return () => {
      abortController.abort();
    };
  }, [currentStep, getHealthUrl]);

  const goToStep = (step: SetupStep) => {
    completeSetupStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      const nextStepId = steps[nextIndex].id;
      completeSetupStep(nextStepId);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStepId = steps[prevIndex].id;
      completeSetupStep(prevStepId);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const markSetupComplete = () => {
    updateSetting("setupCompleted", true);
    updateSetting("setupCompletedAt", new Date().toISOString());
    earnBadge("environment-setup");
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 5000); // Hide after 5 seconds
  };

  const pythonCmd = getPythonCommand(platform);
  const venvPath = getVenvCreatePath(platform);
  const venvActivate = getVenvActivateCommand(platform);
  const shellType = getShellType(platform);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Celebration Toast */}
      {showCelebration && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl shadow-2xl p-6 max-w-md">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-3xl">🚀</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">
                  Achievement Unlocked!
                </h3>
                <p className="text-green-50 text-sm mb-2">
                  You earned the <strong>Environment Setup</strong> badge
                </p>
                <p className="text-xs text-green-100">
                  Check your dashboard to see all your achievements
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Development Environment Setup
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Follow this guide to set up Google ADK and the Visual Agent Builder
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <button
                onClick={() => goToStep(step.id)}
                aria-label={`Go to step ${index + 1}: ${step.title} - ${step.description}`}
                aria-current={index === currentStepIndex ? "step" : undefined}
                className={cn(
                  "flex flex-col items-center gap-2 cursor-pointer group",
                  index <= currentStepIndex ? "opacity-100" : "opacity-40",
                )}
              >
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors",
                    index < currentStepIndex
                      ? "bg-green-500 text-white"
                      : index === currentStepIndex
                        ? "bg-primary-500 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
                  )}
                >
                  {index < currentStepIndex ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span aria-hidden="true">{index + 1}</span>
                  )}
                </div>
                <div className="text-center hidden md:block">
                  <div
                    className={cn(
                      "text-xs font-medium",
                      index <= currentStepIndex
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400",
                    )}
                  >
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {step.description}
                  </div>
                </div>
              </button>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-0.5 flex-1 mx-2",
                    index < currentStepIndex
                      ? "bg-green-500"
                      : "bg-gray-200 dark:bg-gray-700",
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {currentStep === "welcome" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Welcome to ADK Training! 👋
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                This wizard will guide you through setting up your local
                development environment for building AI agents with Google's
                Agent Development Kit (ADK).
              </p>
            </div>

            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-6">
              <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">
                What you'll learn:
              </h3>
              <ul className="space-y-2 text-primary-800 dark:text-primary-200">
                <li className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  How to install and configure Google ADK
                </li>
                <li className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Set up the Visual Agent Builder for hands-on practice
                </li>
                <li className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Verify your installation and troubleshoot issues
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{getPlatformIcon(platform)}</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Detected Platform: {getPlatformName(platform)}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Instructions will be customized for your operating system
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Using {shellType}
              </p>
            </div>

            {/* Video Tutorial Placeholder */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 border-2 border-dashed border-gray-300 dark:border-gray-600">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-primary-600 dark:text-primary-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Video Tutorial
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Watch a 5-minute walkthrough of the setup process
                  </p>
                </div>
              </div>
              <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    Video Coming Soon
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    For now, follow the step-by-step instructions below
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                    Estimated Time: 15-20 minutes
                  </h4>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Make sure you have a stable internet connection and admin
                    rights on your computer
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === "prerequisites" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Prerequisites
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Before we begin, let's make sure you have everything you need.
              Don't worry if you're new to this - we'll guide you through each
              step!
            </p>

            <div className="space-y-4">
              {/* Requirement 1: Terminal Access */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      1
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Terminal Access
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      You'll need to use the {shellType} to run commands.
                    </p>
                    {/* Terminal instructions for beginners */}
                    <TerminalInstructions platform={platform} />
                  </div>
                </div>
              </div>

              {/* Requirement 2: Python */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      2
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Python 3.10 or higher
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Python is a programming language that ADK is built with.
                      Open your terminal and run this command to check if it's
                      installed:
                    </p>
                    <CodeBlock code={`${pythonCmd} --version`} />
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                      <p className="text-xs text-green-700 dark:text-green-300">
                        <span className="font-medium">Expected output:</span>{" "}
                        <code className="bg-green-100 dark:bg-green-900/40 px-1 rounded">
                          Python 3.10.x
                        </code>{" "}
                        or higher (e.g., Python 3.11.4, Python 3.12.0)
                      </p>
                    </div>
                    {/* Python installation helper for beginners */}
                    <div className="mt-4">
                      <PythonInstallHelper platform={platform} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Requirement 3: Google API Key */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      3
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Google API Key
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      An API key is like a special password that lets your
                      programs talk to Google's AI. You'll set this up in a
                      later step, but you can get one now:
                    </p>
                    <a
                      href="https://aistudio.google.com/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors text-sm font-medium"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                      </svg>
                      Get API Key from Google AI Studio
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      You'll need a Google account. The API key is free for
                      getting started.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Encouragement message */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-1">
                    Ready to continue?
                  </h4>
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Once you've confirmed Python is installed and you can open
                    your terminal, click "Next Step" to continue. Don't worry
                    about the API key yet - we'll set that up later!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === "venv" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Virtual Environment
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Before installing ADK, we'll create an isolated workspace for your
              project.
            </p>

            {/* What is a virtual environment explainer */}
            <ExpandableSection
              title="What is a virtual environment and why do I need one?"
              variant="help"
              icon={
                <svg
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            >
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Think of a virtual environment like a{" "}
                  <strong>separate toolbox</strong> for each project you work
                  on. Just like a carpenter might have different toolboxes for
                  different jobs, Python projects need their own set of tools
                  (called "packages").
                </p>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Without a virtual environment:</strong> All your
                    projects share the same tools. If one project needs version
                    1.0 of a tool and another needs version 2.0, they'll
                    conflict!
                  </p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    <strong>With a virtual environment:</strong> Each project
                    has its own toolbox. They can use different versions without
                    any conflicts. Clean and organized!
                  </p>
                </div>
              </div>
            </ExpandableSection>

            <div className="space-y-4">
              {/* Step 1: Create */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      1
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Create the virtual environment
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      This creates a new folder to hold your project's tools
                    </p>
                  </div>
                </div>
                <CodeBlock
                  code={`${pythonCmd} -m venv ${venvPath}`}
                  title="Create virtual environment"
                />

                {/* Command breakdown */}
                <div className="mt-3">
                  <ExpandableSection
                    title="What does this command mean?"
                    variant="info"
                    icon={
                      <svg
                        className="w-4 h-4 text-purple-600 dark:text-purple-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                  >
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                          {pythonCmd}
                        </code>
                        <span className="text-gray-600 dark:text-gray-400">
                          Runs the Python program
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                          -m venv
                        </code>
                        <span className="text-gray-600 dark:text-gray-400">
                          Uses Python's built-in virtual environment creator
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                          {venvPath}
                        </code>
                        <span className="text-gray-600 dark:text-gray-400">
                          The folder name for your virtual environment (in your
                          home directory)
                        </span>
                      </div>
                    </div>
                  </ExpandableSection>
                </div>
              </div>

              {/* Step 2: Activate */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      2
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Activate the virtual environment
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      This tells your terminal to use the new toolbox
                    </p>
                  </div>
                </div>
                <CodeBlock
                  code={venvActivate}
                  title="Activate virtual environment"
                />

                {/* Success indicator */}
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        How do I know it worked?
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                        You'll see{" "}
                        <code className="bg-green-100 dark:bg-green-900/40 px-1.5 py-0.5 rounded font-mono">
                          (adk-workshop)
                        </code>{" "}
                        appear at the beginning of your terminal prompt. This
                        means you're now "inside" the virtual environment!
                      </p>
                    </div>
                  </div>
                </div>

                {/* Visual example */}
                <div className="mt-3 p-3 bg-gray-900 rounded-lg">
                  <p className="text-xs text-gray-400 mb-2">
                    Example of what you'll see:
                  </p>
                  <div className="font-mono text-sm">
                    <div className="text-gray-500">Before activation:</div>
                    <div className="text-green-400 ml-2">
                      {platform === "windows" ? "C:\\Users\\you>" : "~$"}{" "}
                      <span className="animate-pulse">|</span>
                    </div>
                    <div className="text-gray-500 mt-2">After activation:</div>
                    <div className="text-green-400 ml-2">
                      <span className="text-yellow-400">(adk-workshop)</span>{" "}
                      {platform === "windows" ? "C:\\Users\\you>" : "~$"}{" "}
                      <span className="animate-pulse">|</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important reminder */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                      Remember this for later!
                    </h4>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Every time you open a new terminal window to work on ADK,
                      you'll need to run the activate command again. The virtual
                      environment doesn't stay active between terminal sessions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <TroubleshootingSection
                items={
                  [
                    {
                      error:
                        platform === "windows"
                          ? "The term 'python' is not recognized"
                          : "command not found: python3",
                      errorDescription:
                        "Python isn't installed or isn't in your PATH",
                      solution:
                        "Go back to the Prerequisites step and install Python. Make sure to check 'Add Python to PATH' during installation (Windows).",
                    },
                    {
                      error:
                        platform === "windows"
                          ? `The system cannot find the path specified: ${venvPath}\\Scripts\\activate`
                          : `No such file or directory: ${venvPath}/bin/activate`,
                      errorDescription:
                        "The virtual environment wasn't created properly",
                      solution:
                        "Run the create command again. Make sure you're using the correct Python command for your system.",
                      command: `${pythonCmd} -m venv ${venvPath}`,
                    },
                    {
                      error:
                        platform === "windows"
                          ? "cannot be loaded because running scripts is disabled"
                          : "Permission denied",
                      errorDescription:
                        platform === "windows"
                          ? "PowerShell's execution policy is blocking scripts"
                          : "You don't have permission to run this command",
                      solution:
                        platform === "windows"
                          ? "Open PowerShell as Administrator and run: Set-ExecutionPolicy -ExecutionPolicy RemoteSigned -Scope CurrentUser"
                          : "Try running with sudo, or check the file permissions.",
                    },
                    {
                      error: "(adk-workshop) doesn't appear in my prompt",
                      errorDescription:
                        "The activation might have failed silently",
                      solution:
                        "Make sure you typed the command exactly as shown. On Windows, use backslashes (\\). On Mac/Linux, use forward slashes (/).",
                    },
                  ] satisfies TroubleshootingItem[]
                }
              />
            </div>
          </div>
        )}

        {currentStep === "install-adk" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Install Google ADK
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Now let's install the Agent Development Kit. Make sure your
              virtual environment is activated (you should see{" "}
              <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-sm">
                (adk-workshop)
              </code>{" "}
              in your terminal).
            </p>

            <div className="space-y-4">
              {/* Step 1: Install */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      1
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Install the ADK package
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      This downloads and installs Google ADK and everything it
                      needs
                    </p>
                  </div>
                </div>
                <CodeBlock
                  code="pip install google-adk"
                  title="Install Google ADK"
                />

                {/* What is pip explainer */}
                <div className="mt-3">
                  <ExpandableSection
                    title="What is pip?"
                    variant="info"
                    icon={
                      <svg
                        className="w-4 h-4 text-purple-600 dark:text-purple-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                  >
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>pip</strong> is Python's package installer -
                        think of it like an "app store" for Python tools. When
                        you run{" "}
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">
                          pip install something
                        </code>
                        , it downloads that package from the internet and sets
                        it up for you.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        The name "pip" stands for "Pip Installs Packages" (yes,
                        it's a recursive acronym - programmers love those!).
                      </p>
                    </div>
                  </ExpandableSection>
                </div>

                {/* What to expect */}
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        What to expect
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        You'll see a lot of text scrolling by as packages
                        download. This is normal! It may take 1-3 minutes
                        depending on your internet speed. Wait until you see
                        your prompt return.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Verify */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">
                      2
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Verify the installation
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Let's make sure ADK was installed correctly
                    </p>
                  </div>
                </div>
                <CodeBlock
                  code="pip show google-adk"
                  title="Check ADK installation"
                />

                {/* Expected output */}
                <div className="mt-3">
                  <ExpandableSection
                    title="What should I see?"
                    variant="help"
                    defaultExpanded={true}
                    icon={
                      <svg
                        className="w-4 h-4 text-blue-600 dark:text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                  >
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        You should see information about the package, including:
                      </p>
                      <div className="p-3 bg-gray-900 rounded-lg font-mono text-xs text-gray-100 overflow-x-auto">
                        <div>
                          <span className="text-gray-500">Name:</span>{" "}
                          google-adk
                        </div>
                        <div>
                          <span className="text-gray-500">Version:</span>{" "}
                          <span className="text-green-400">1.x.x</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Summary:</span> Google
                          Agent Development Kit
                        </div>
                        <div>
                          <span className="text-gray-500">Home-page:</span> ...
                        </div>
                        <div className="text-gray-500">...</div>
                      </div>
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                        <p className="text-xs text-green-700 dark:text-green-300">
                          If you see a version number, you're good to go!
                        </p>
                      </div>
                    </div>
                  </ExpandableSection>
                </div>
              </div>

              {/* Step 3: Install Save Patch (Recommended) */}
              <div className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">
                      3
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Install Visual Builder Save Fix (Recommended)
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Fixes a known bug where Visual Builder changes don't save
                      properly
                    </p>
                  </div>
                </div>
                <CodeBlock
                  code="pip install git+https://github.com/Boatschool/adk-save-patch.git"
                  title="Install save fix"
                />
                <div className="mt-3">
                  <ExpandableSection
                    title="Why do I need this?"
                    variant="info"
                    icon={
                      <svg
                        className="w-4 h-4 text-purple-600 dark:text-purple-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                  >
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        There's a known bug in Google ADK's Visual Builder where
                        clicking "Save" doesn't actually save your changes. This
                        patch fixes that issue so your work is properly saved.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>Without this fix:</strong> You might lose your
                        work when editing agents in the Visual Builder.
                      </p>
                      <a
                        href="https://github.com/google/adk-python/issues/3666"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                      >
                        Learn more about this issue (GitHub #3666)
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  </ExpandableSection>
                </div>
              </div>

              {/* What is ADK explainer */}
              <ExpandableSection
                title="What exactly is the Agent Development Kit (ADK)?"
                variant="info"
                icon={
                  <svg
                    className="w-4 h-4 text-purple-600 dark:text-purple-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                }
              >
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Google's <strong>Agent Development Kit (ADK)</strong> is a
                    set of tools that makes it easy to build AI agents -
                    programs that can understand natural language, make
                    decisions, and take actions.
                  </p>
                  <div className="grid gap-2">
                    <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Visual Builder:</strong> A graphical interface
                        to design agents without writing much code
                      </p>
                    </div>
                    <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Pre-built Components:</strong> Ready-to-use
                        building blocks for common AI tasks
                      </p>
                    </div>
                    <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Google AI Integration:</strong> Easy access to
                        Google's powerful language models
                      </p>
                    </div>
                  </div>
                </div>
              </ExpandableSection>

              {/* Success message */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100 mb-1">
                      Almost there!
                    </h4>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      Once you see the version number, ADK is installed! Click
                      "Next Step" to configure your API key so ADK can talk to
                      Google's AI.
                    </p>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <TroubleshootingSection
                items={
                  [
                    {
                      error: "pip: command not found",
                      errorDescription:
                        "pip isn't available in your current environment",
                      solution:
                        "Make sure your virtual environment is activated. You should see (adk-workshop) in your prompt.",
                      command: venvActivate,
                    },
                    {
                      error:
                        "ERROR: Could not find a version that satisfies the requirement",
                      errorDescription:
                        "The package name might be wrong or your Python version is too old",
                      solution:
                        "Make sure you're using Python 3.10 or higher. Check with: " +
                        pythonCmd +
                        " --version",
                    },
                    {
                      error:
                        "WARNING: pip is configured with locations that require TLS/SSL",
                      errorDescription:
                        "Your Python installation is missing SSL support",
                      solution:
                        "Reinstall Python from python.org, making sure to get the latest version for your platform.",
                    },
                    {
                      error:
                        "pip show google-adk shows nothing / Package not found",
                      errorDescription:
                        "The installation failed or you're in a different environment",
                      solution:
                        "Make sure your virtual environment is activated, then try installing again: pip install google-adk",
                    },
                    {
                      error:
                        "ERROR: Could not install packages due to an OSError",
                      errorDescription:
                        "Permission issues or disk space problems",
                      solution:
                        platform === "windows"
                          ? "Try running your terminal as Administrator, or check that you have enough disk space."
                          : "Try using: pip install --user google-adk",
                    },
                  ] satisfies TroubleshootingItem[]
                }
              />
            </div>
          </div>
        )}

        {currentStep === "api-key" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Configure Your API Key
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your API key lets the ADK connect to Google's AI. Let's get it set
              up!
            </p>

            {/* Step 1: Get API Key */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 dark:text-primary-400 font-bold">
                    1
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Get your API key from Google AI Studio
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    If you haven't already, create a free API key
                  </p>
                </div>
              </div>

              <a
                href="https://aistudio.google.com/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                Open Google AI Studio
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>

              {/* How to get API key walkthrough */}
              <div className="mt-4">
                <ExpandableSection
                  title="How do I create an API key?"
                  variant="help"
                  icon={
                    <svg
                      className="w-4 h-4 text-blue-600 dark:text-blue-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                >
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                        1
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Click the link above to open Google AI Studio
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                        2
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Sign in with your Google account (or create one if
                        needed)
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                        3
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Click the <strong>"Create API Key"</strong> button
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                        4
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Copy your new API key (it looks like{" "}
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">
                          AIza...
                        </code>
                        )
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                        5
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Come back here and paste it below!
                      </span>
                    </li>
                  </ol>
                </ExpandableSection>
              </div>
            </div>

            {/* Step 2: Save API Key */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 dark:text-primary-400 font-bold">
                    2
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Save your API key
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Paste your key below to save it securely in your profile
                  </p>
                </div>
              </div>

              <ApiKeyInput
                value={settings.googleApiKey}
                onChange={(key) => updateSetting("googleApiKey", key)}
              />
            </div>

            {/* Step 3: Set Environment Variable */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 dark:text-primary-400 font-bold">
                    3
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Set the environment variable
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Run this in your terminal (with your virtual environment
                    activated)
                  </p>
                </div>
              </div>

              <CodeBlock
                code={
                  platform === "windows"
                    ? `$env:GOOGLE_GENAI_USE_VERTEXAI = "FALSE"\n$env:GOOGLE_API_KEY = "${settings.googleApiKey || "your-api-key-here"}"`
                    : `export GOOGLE_GENAI_USE_VERTEXAI=FALSE\nexport GOOGLE_API_KEY=${settings.googleApiKey || "your-api-key-here"}`
                }
                title={
                  platform === "windows" ? "Windows PowerShell" : "Terminal"
                }
              />

              {settings.googleApiKey && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Your saved API key has been inserted into the command above!
                </p>
              )}

              {/* What is an environment variable */}
              <div className="mt-4">
                <ExpandableSection
                  title="What is an environment variable?"
                  variant="info"
                  icon={
                    <svg
                      className="w-4 h-4 text-purple-600 dark:text-purple-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                >
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    An environment variable is like a sticky note for your
                    computer. It stores a piece of information (like your API
                    key) that programs can read when they need it. The{" "}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">
                      export
                    </code>{" "}
                    command creates this "sticky note" in your terminal session.
                  </p>
                </ExpandableSection>
              </div>
            </div>

            {/* Success indicator */}
            {settings.googleApiKey && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100 mb-1">
                      Great progress!
                    </h4>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      Your API key is saved. Run the command above in your
                      terminal, then click "Next Step" to verify everything is
                      working.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === "verify" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Verify Installation
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Let's test that everything is working by starting the Visual
              Builder.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Start the Visual Builder
                </h3>
                <CodeBlock
                  code="adk web --port 8000"
                  title="Start ADK Visual Builder"
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  The server will start on port 8000. Keep this terminal window
                  open.
                </p>
              </div>

              <div
                className={cn(
                  "border-2 rounded-lg p-6 transition-colors",
                  builderConnected
                    ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800"
                    : "bg-gray-50 dark:bg-gray-900/50 border-gray-300 dark:border-gray-700",
                )}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center",
                      builderConnected
                        ? "bg-green-100 dark:bg-green-900/40"
                        : "bg-gray-200 dark:bg-gray-700",
                    )}
                  >
                    {builderConnected ? (
                      <svg
                        className="w-6 h-6 text-green-600 dark:text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-gray-400 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3
                      className={cn(
                        "font-semibold text-lg",
                        builderConnected
                          ? "text-green-900 dark:text-green-100"
                          : "text-gray-900 dark:text-white",
                      )}
                    >
                      {builderConnected
                        ? "Visual Builder Connected! ✓"
                        : "Waiting for Visual Builder..."}
                    </h3>
                    <p
                      className={cn(
                        "text-sm",
                        builderConnected
                          ? "text-green-700 dark:text-green-300"
                          : "text-gray-600 dark:text-gray-400",
                      )}
                    >
                      {builderConnected
                        ? `Running at ${settings.localBuilderUrl}`
                        : "Start the server using the command above"}
                    </p>
                  </div>
                </div>

                {builderConnected && (
                  <a
                    href={`${settings.localBuilderUrl}/dev-ui`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Open Visual Builder
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}
              </div>

              {/* Troubleshooting - always visible but more prominent when not connected */}
              <TroubleshootingSection
                title="Visual Builder not starting? Common issues"
                defaultExpanded={!builderConnected}
                items={
                  [
                    {
                      error: "adk: command not found",
                      errorDescription:
                        "ADK isn't installed or your virtual environment isn't active",
                      solution:
                        "Make sure your virtual environment is activated (you should see (adk-workshop) in your prompt), then try: pip install google-adk",
                    },
                    {
                      error: "Address already in use / Port 8000 is in use",
                      errorDescription:
                        "Another application is using port 8000",
                      solution:
                        "Either close the other application, or use a different port:",
                      command: "adk web --port 8001",
                    },
                    {
                      error: "GOOGLE_API_KEY environment variable not set",
                      errorDescription:
                        "The API key isn't configured in your terminal session",
                      solution:
                        "Run the export command from the previous step again. Remember, you need to set this each time you open a new terminal.",
                      command:
                        platform === "windows"
                          ? "set GOOGLE_API_KEY=your-key-here"
                          : "export GOOGLE_API_KEY=your-key-here",
                    },
                    {
                      error: "Invalid API key / 401 Unauthorized",
                      errorDescription:
                        "Your API key is incorrect or has been revoked",
                      solution:
                        "Go to Google AI Studio and create a new API key, then update your environment variable.",
                    },
                    {
                      error:
                        "Connection refused / Cannot connect to localhost:8000",
                      errorDescription:
                        "The server isn't running or is on a different port",
                      solution:
                        "Make sure the adk web command is still running in your terminal. Look for any error messages in that terminal window.",
                    },
                    {
                      error: "ModuleNotFoundError: No module named 'google'",
                      errorDescription:
                        "ADK dependencies aren't installed properly",
                      solution: "Reinstall ADK in your virtual environment:",
                      command: "pip install --upgrade google-adk",
                    },
                  ] satisfies TroubleshootingItem[]
                }
              />

              {/* What to expect tip */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                      What to expect when it works
                    </h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      When the Visual Builder starts successfully, you'll see a
                      message like "INFO: Uvicorn running on
                      http://0.0.0.0:8000" in your terminal. This page will
                      automatically detect when it's running!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === "complete" && (
          <div className="space-y-6 text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
              <svg
                className="w-10 h-10 text-green-600 dark:text-green-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Setup Complete! 🎉
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Your development environment is ready for building AI agents
              </p>
            </div>

            {builderConnected ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  ✓ Visual Builder is running
                </h3>
                <p className="text-sm text-green-800 dark:text-green-200 mb-4">
                  You're all set to start building agents!
                </p>
                <a
                  href={`${settings.localBuilderUrl}/dev-ui`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                >
                  Launch Visual Builder
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Remember to start the Visual Builder with{" "}
                  <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">
                    adk web --port 8000
                  </code>
                </p>
              </div>
            )}

            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Next Steps
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <Link
                  to="/workshops"
                  className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <svg
                      className="w-6 h-6 text-primary-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    <span className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
                      Start a Workshop
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Learn agent development through hands-on workshops
                  </p>
                </Link>

                <Link
                  to="/guides/visual-agent-builder-guide"
                  className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <svg
                      className="w-6 h-6 text-primary-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
                      Read the Guides
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Explore detailed guides and reference documentation
                  </p>
                </Link>
              </div>
            </div>

            {!settings.setupCompleted && (
              <button
                onClick={markSetupComplete}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Mark setup as complete
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={prevStep}
          disabled={currentStepIndex === 0}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
            currentStepIndex === 0
              ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800",
          )}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Previous
        </button>

        {currentStepIndex < steps.length - 1 ? (
          <button
            onClick={nextStep}
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors"
          >
            Next Step
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        ) : (
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors"
          >
            Go to Dashboard
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
}
